import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import DarkModeToggle from 'front/components/ui/DarkModeToggle';
import { useTheme } from '../../../core/contexts/ThemeContext';
import apiClient from '../../../core/http/axios';
import ExpirationModal from 'front/components/ui/ExpirationModal';
import { getUniversities, getDepartments, University, Department } from '../../services/commonService';
import {
  validatePassword,
  validatePhone,
  validateStudentNumber,
  validateEmail,
  validateName,
  formatStudentNumberInput,
  formatEmailInput,
  getReadableErrorMessage,
} from '../../utils/formValidation';

const Container = styled.div`
  position: relative;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  min-height: 100vh;
  background:
    url('/images/global/member_bg_bottom1.png') no-repeat left bottom,
    url('/images/global/member_bg_bottom2.png') no-repeat right bottom,
    url('/images/global/member_bg.png') no-repeat center center;
  background-size: auto, auto, cover;

  html.dark-mode & {
    background:
      url('/images/global/member_bg_bottom1_dark.png') no-repeat left bottom,
      url('/images/global/member_bg_bottom2_dark.png') no-repeat right bottom,
      url('/images/global/member_bg_dark.png') no-repeat center center;
  }
`;

const Header = styled.header`
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  & button {
    pointer-events: auto;
  }
`;

const Logo = styled.div`
  cursor: pointer;
  pointer-events: auto;
  html.dark-mode & img {
    filter: invert(1);
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 320px;
  gap: ${props => props.theme.spacing['4xl']};
  pointer-events: auto;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: ${props => props.theme.colors.book.color1b};
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing['2xl']};
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${props => props.theme.spacing.xs};
`;

const LabelText = styled.label`
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: ${props => props.theme.colors.book.color6b};
  & > .required {
    color: #DF0707;
    font-size: ${props => props.theme.fontSize.xs};
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
  width: 100%;
  &.row {
    flex-flow: row nowrap;
    align-items: center;
  }
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 320px;
  & input {
    flex: 1 1 0;
    min-width: 0;
    max-width: 90px;
    text-align: center;
  }
`;

const Input = styled.input<{ $hasError: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.border.de};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.base};
  color: ${props => props.theme.colors.book.color2b};
  transition: all 0.2s;
  height: 44px;
  width: 100%;
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error[500] : props.theme.colors.toss.blue};
  }

  &::placeholder {
    color: ${props => props.theme.colors.gray[400]};
  }

  .row & {
    width: auto;
    flex: 1;
  }
`;

const Select = styled.select<{ $hasError: boolean }>`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$hasError ? props.theme.colors.error[300] : props.theme.colors.white};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.base};
  color: var(--text-primary);
  transition: all 0.2s;
  height: 44px;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? props.theme.colors.error[500] : props.theme.colors.toss.blue};
  }
`;

const SearchButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border.de};
  background-color: var(--bg-secondary);
  font-size: ${props => props.theme.fontSize.sm};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  width: 44px;

  &:hover {
    background-color: ${props => props.theme.colors.gray[50]};
    border-color: ${props => props.theme.colors.gray[400]};
  }
`;

const VerifyButton = styled.button<{ $disabled: boolean; $verified: boolean }>`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.$verified ? props.theme.colors.success[500] : props.theme.colors.gray[300]};
  background-color: ${props => props.$verified ? props.theme.colors.success[500] : props.$disabled ? props.theme.colors.white: props.theme.colors.toss.blue};
  color: ${props => props.$verified ? props.theme.colors.white : props.$disabled ? props.theme.colors.book.color2b : props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  white-space: nowrap;
  height: 44px;
  width: 100%;
  font-weight: ${props => props.theme.fontWeight.semibold};

  &:hover:not(:disabled) {
    background-color: ${props => props.$verified ? props.theme.colors.success[600] : props.theme.colors.primary[700]};
    border-color: ${props => props.$verified ? props.theme.colors.success[600] : props.theme.colors.primary[700]};
    color: ${props => props.$verified ? props.theme.colors.white : props.theme.colors.white};
  }
`;

const VerifyTimer = styled.div`
  position: absolute;
  right: 16px;
  top: 22px;
  color: ${props => props.theme.colors.primary[600]};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.normal};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ValidationMessage = styled.div<{ $isError: boolean }>`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.$isError ? props.theme.colors.error[500] : props.theme.colors.success[600]};
`;

const HelperText = styled.div`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[500]};
  margin-left: auto;
  margin-top: ${props => props.theme.spacing.xs};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: auto;
`;

const ModalContainer = styled.div`
  background-color: var(--bg-secondary);
  border-radius: ${props => props.theme.borderRadius.xl};
  padding: ${props => props.theme.spacing['2xl']};
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme.shadows.modal};
`;

const ModalTitle = styled.h2`
  font-size: ${props => props.theme.fontSize.xl};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: var(--text-primary);
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  font-size: ${props => props.theme.fontSize.base};
  color: var(--text-primary);
  margin-bottom: ${props => props.theme.spacing.lg};
  height: 44px;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.toss.blue};
  }
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ListItem = styled.div<{ $selected: boolean }>`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$selected ? props.theme.colors.toss.blue : 'transparent'};
  color: ${props => props.$selected ? props.theme.colors.white : 'var(--text-primary)'};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSize.sm};
  &:hover {
    background-color: ${props => props.$selected ? props.theme.colors.primary[700] : props.theme.colors.gray[100]};
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
`;

const ModalButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  height: 44px;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: none;
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background-color: ${props.theme.colors.toss.blue};
    color: ${props.theme.colors.white};
    
    &:hover {
      background-color: ${props.theme.colors.primary[700]};
    }
  ` : `
    background-color: ${props.theme.colors.gray[100]};
    color: var(--text-primary);
    
    &:hover {
      background-color: ${props.theme.colors.gray[200]};
    }
  `}
`;

const LoadingText = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['2xl']};
  color: ${props => props.theme.colors.gray[500]};
`;

const ErrorText = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing['2xl']};
  color: ${props => props.theme.colors.error[500]};
`;

const NextButton = styled.button<{ $disabled: boolean }>`
  width: 100%;
  max-width: 600px;
  height: 44px;
  padding: ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: none;
  background-color: ${props => props.$disabled ? props.theme.colors.book.color0b : props.theme.colors.toss.blue};
  color: ${props => props.theme.colors.white};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: ${props => props.$disabled ? 'none' : props.theme.shadows.sm};

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows.md};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // ID 찾기에서 전달받은 ID 값
  const foundId = (location.state as any)?.id || '';
  const [originalEmail, setOriginalEmail] = useState<string>('');
  
  const [formData, setFormData] = useState({
    id: foundId,
    password: '',
    confirmPassword: '',
    name: '',
    school: '',
    department: '',
    universityNo: 0,
    departmentNo: 0,
    studentId: '',
    emailId: '',
    emailDomain: '',
    phone1: '010',
    phone2: '',
    phone3: '',
  });
  const [emailDomainType, setEmailDomainType] = useState<'select' | 'input'>('select');
  const [selectedDomainOption, setSelectedDomainOption] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailVerification, setEmailVerification] = useState({
    requestUuid: '',
    verificationCode: '',
    isRequested: false,
    isVerified: false,
    isRequesting: false,
    isVerifying: false,
    error: '',
    countdown: 0,
    isExpired: false,
  });
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showExpirationModal, setShowExpirationModal] = useState(false);
  const [showUniversityModal, setShowUniversityModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [universities, setUniversities] = useState<University[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [universitySearch, setUniversitySearch] = useState('');
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [isLoadingUniversities, setIsLoadingUniversities] = useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
  const [universityError, setUniversityError] = useState('');
  const [departmentError, setDepartmentError] = useState('');

  const [validation, setValidation] = useState({
    id: { isValid: false, message: '', isChecking: false },
    password: { isValid: false, message: '' },
    confirmPassword: { isValid: false, message: '' },
    name: { isValid: false, message: '' },
    studentId: { isValid: false, message: '' },
    phone: { isValid: false, message: '' },
  });

  const checkLoginIdAvailability = async (loginId: string): Promise<{ available: boolean }> => {
    try {
      const response = await apiClient.get<{ success: boolean; error: null; data: { available: boolean }; code: null; meta: { elapsed_ms: number } }>(
        `/auth/student/check-login-id`,
        {
          params: {
            login_id: loginId,
          },
        }
      );
      return response.data.data;
    } catch (error: any) {
      // console.error('ID 중복 체크 오류:', error);
      throw error;
    }
  };

  const validateId = async (id: string) => {
    if (id.length < 8) {
      return { isValid: false, message: '', isChecking: false };
    }
    
    setValidation(prev => ({ ...prev, id: { ...prev.id, isChecking: true } }));
    
    try {
      const result = await checkLoginIdAvailability(id);
      if (result.available) {
        return { isValid: true, message: '사용가능한 ID 입니다.', isChecking: false };
      } else {
        return { isValid: false, message: '이미 사용 중인 ID 입니다.', isChecking: false };
      }
    } catch (error) {
      return { isValid: false, message: 'ID 중복 확인 중 오류가 발생했습니다.', isChecking: false };
    }
  };

  const validateConfirmPassword = (confirmPassword: string, password: string) => {
    if (confirmPassword !== password) {
      return { isValid: false, message: '비밀번호를 동일하게 작성해주시기 바랍니다.' };
    }
    if (confirmPassword && password && confirmPassword === password) {
      return { isValid: true, message: '확인되었습니다.' };
    }
    return { isValid: false, message: '' };
  };

  const handleIdChange = (value: string) => {
    setFormData(prev => ({ ...prev, id: value }));
    setValidation(prev => ({ ...prev, id: { ...prev.id, message: '', isValid: false } }));
  };

  const handleIdBlur = async () => {
    if (formData.id.length === 0) return;
    if (formData.id.length < 8) {
      setValidation(prev => ({ ...prev, id: { isValid: false, message: '8글자 이상의 아이디를 입력해주세요.', isChecking: false } }));
      return;
    }
    const result = await validateId(formData.id);
    setValidation(prev => ({ ...prev, id: result }));
  };

  const handlePasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, password: value }));
    setValidation(prev => ({ ...prev, password: value ? validatePassword(value) : { isValid: false, message: '' } }));
    // confirmPassword 재검증
    if (formData.confirmPassword) {
      setValidation(prev => ({
        ...prev,
        confirmPassword: validateConfirmPassword(formData.confirmPassword, value),
      }));
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setFormData(prev => ({ ...prev, confirmPassword: value }));
    if (value) {
      setValidation(prev => ({
        ...prev,
        confirmPassword: validateConfirmPassword(value, formData.password),
      }));
    } else {
      setValidation(prev => ({ ...prev, confirmPassword: { isValid: false, message: '' } }));
    }
  };

  const handleStudentIdChange = (value: string) => {
    const numericValue = formatStudentNumberInput(value);
    setFormData(prev => ({ ...prev, studentId: numericValue }));
    setValidation(prev => ({ ...prev, studentId: numericValue ? validateStudentNumber(numericValue) : { isValid: false, message: '' } }));
  };

  const getCombinedPhone = () => formData.phone1 + formData.phone2 + formData.phone3;

  const handlePhonePartChange = (part: 1 | 2 | 3, value: string) => {
    const digitsOnly = value.replace(/[^0-9]/g, '');
    const maxLen = part === 1 ? 3 : 4;
    const truncated = digitsOnly.slice(0, maxLen);
    setFormData(prev => ({ ...prev, [`phone${part}`]: truncated }));
    const nextPhone = part === 1 ? truncated + formData.phone2 + formData.phone3
      : part === 2 ? formData.phone1 + truncated + formData.phone3
      : formData.phone1 + formData.phone2 + truncated;
    setValidation(prev => ({ ...prev, phone: nextPhone.length === 11 ? validatePhone(nextPhone) : { isValid: false, message: '' } }));
  };

  const emailDomains = ['gmail.com', 'naver.com', 'daum.net', 'hanmail.net', 'kakao.com', '직접입력'];

  const handleEmailDomainChange = (value: string) => {
    setSelectedDomainOption(value);
    if (value === '직접입력') {
      setEmailDomainType('input');
      setFormData(prev => ({ ...prev, emailDomain: '' }));
    } else if (value) {
      setEmailDomainType('select');
      setFormData(prev => ({ ...prev, emailDomain: value }));
    }
  };

  const getFullEmail = () => {
    if (formData.emailId && formData.emailDomain) {
      return `${formData.emailId}@${formData.emailDomain}`;
    }
    return '';
  };

  const handleEmailVerificationRequest = async () => {
    const email = getFullEmail();
    if (!email) {
      setEmailVerification(prev => ({ ...prev, error: '이메일을 입력해주세요.' }));
      return;
    }

    const emailValidationResult = validateEmail(email);
    if (!emailValidationResult.isValid) {
      setEmailVerification(prev => ({ ...prev, error: emailValidationResult.message }));
      return;
    }

    setEmailVerification(prev => ({ ...prev, isRequesting: true, error: '' }));

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: {
          request_uuid: string;
        };
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/email/request', {
        email: email,
      });

      if (response.data.success && response.data.data.request_uuid) {
        // 원본 이메일 저장
        setOriginalEmail(email);
        // 카운트다운 시작 (3분 = 180초)
        setEmailVerification(prev => ({
          ...prev,
          requestUuid: response.data.data.request_uuid,
          isRequested: true,
          isRequesting: false,
          error: '',
          countdown: 180,
          isExpired: false,
        }));
      } else {
        const apiError = response.data.error;
        const errorMessage = apiError === 'EMAIL_ALREADY_USED'
          ? '이미 사용중인 이메일입니다.'
          : (apiError || '인증 요청에 실패했습니다.');
        setEmailVerification(prev => ({
          ...prev,
          isRequesting: false,
          error: errorMessage,
        }));
      }
    } catch (error: any) {
      const errorMessage = getReadableErrorMessage(error, '이메일 인증 요청 중 오류가 발생했습니다.');
      setEmailVerification(prev => ({
        ...prev,
        isRequesting: false,
        error: errorMessage,
      }));
    }
  };

  const handleEmailVerificationVerify = async () => {
    if (!emailVerification.requestUuid || !emailVerification.verificationCode) {
      setEmailVerification(prev => ({ ...prev, error: '인증 코드를 입력해주세요.' }));
      return;
    }

    setEmailVerification(prev => ({ ...prev, isVerifying: true, error: '' }));

    try {
      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: {
          verified: boolean;
        };
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/email/verify', {
        request_uuid: emailVerification.requestUuid,
        code: emailVerification.verificationCode,
      });

      if (response.data.success && response.data.data?.verified) {
        setEmailVerification(prev => ({
          ...prev,
          isVerified: true,
          isVerifying: false,
          error: '',
        }));
      } else {
        setEmailVerification(prev => ({
          ...prev,
          isVerifying: false,
          error: response.data.error || '인증에 실패했습니다.',
        }));
      }
    } catch (error: any) {
      const errorMessage = getReadableErrorMessage(error, '인증 확인 중 오류가 발생했습니다.');
      setEmailVerification(prev => ({
        ...prev,
        isVerifying: false,
        error: errorMessage,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid() || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const email = `${formData.emailId}@${formData.emailDomain}`;
      
      const signupData = {
        login_id: formData.id,
        password: formData.password,
        password_confirm: formData.confirmPassword,
        name: formData.name,
        university_no: formData.universityNo > 0 ? formData.universityNo : 1,
        department_no: formData.departmentNo > 0 ? formData.departmentNo : 1,
        student_number: formData.studentId,
        email: email,
        email_request_uuid: emailVerification.requestUuid,
        tel: getCombinedPhone(),
      };

      const response = await apiClient.post<{
        success: boolean;
        error: string | null;
        data: {
          user_no: number;
          role: string;
        };
        code: string | null;
        meta: {
          elapsed_ms: number;
        };
      }>('/auth/student/signup', signupData);

      if (response.data.success) {
        navigate('/register/complete');
      } else {
        alert(response.data.error || '회원가입에 실패했습니다.');
      }
    } catch (error: any) {
      const errorMessage = getReadableErrorMessage(error, '회원가입 중 오류가 발생했습니다.');
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (foundId && foundId.length >= 8) {
      validateId(foundId).then(result => setValidation(prev => ({ ...prev, id: result })));
    }
  }, []);

  // 카운트다운 관리
  useEffect(() => {
    if (emailVerification.isRequested && emailVerification.countdown > 0 && !emailVerification.isVerified) {
      countdownTimerRef.current = setInterval(() => {
        setEmailVerification(prev => {
          if (prev.countdown <= 1) {
            // 만료 처리
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            setShowExpirationModal(true);
            return {
              ...prev,
              countdown: 0,
              isExpired: true,
            };
          }
          return {
            ...prev,
            countdown: prev.countdown - 1,
          };
        });
      }, 1000);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [emailVerification.isRequested, emailVerification.countdown, emailVerification.isVerified]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendVerification = async () => {
    setShowExpirationModal(false);
    setEmailVerification(prev => ({
      ...prev,
      isRequested: false,
      isExpired: false,
      verificationCode: '',
      requestUuid: '',
      countdown: 0,
    }));
    await handleEmailVerificationRequest();
  };

  const handleUniversitySearchClick = async () => {
    setShowUniversityModal(true);
    setUniversitySearch('');
    setIsLoadingUniversities(true);
    setUniversityError('');

    try {
      const data = await getUniversities();
      setUniversities(data);
    } catch (error: any) {
      // console.error('학교 목록 조회 오류:', error);
      setUniversityError('학교 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingUniversities(false);
    }
  };

  const handleDepartmentSearchClick = async () => {
    if (!formData.universityNo || formData.universityNo === 0) {
      alert('먼저 학교를 선택해주세요.');
      return;
    }

    setShowDepartmentModal(true);
    setDepartmentSearch('');
    setIsLoadingDepartments(true);
    setDepartmentError('');

    try {
      const data = await getDepartments(formData.universityNo);
      setDepartments(data);
    } catch (error: any) {
      // console.error('학과 목록 조회 오류:', error);
      setDepartmentError('학과 목록을 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingDepartments(false);
    }
  };

  const handleUniversitySelect = (university: University) => {
    setFormData(prev => ({
      ...prev,
      school: university.name,
      universityNo: university.university_no,
      department: '',
      departmentNo: 0,
    }));
    setShowUniversityModal(false);
  };

  const handleDepartmentSelect = (department: Department) => {
    setFormData(prev => ({
      ...prev,
      department: department.name,
      departmentNo: department.department_no,
    }));
    setShowDepartmentModal(false);
  };

  const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(universitySearch.toLowerCase())
  );

  const filteredDepartments = departments.filter(dept =>
    dept.name.toLowerCase().includes(departmentSearch.toLowerCase())
  );

  // 이메일이 변경되었는지 확인
  const isEmailChanged = () => {
    const currentEmail = getFullEmail();
    return originalEmail && currentEmail && currentEmail !== originalEmail;
  };

  const isFormValid = () => {
    const email = getFullEmail();
    // 이메일이 변경되었으면 재인증 필요, 변경되지 않았으면 기존 인증 상태 유지
    const emailVerificationRequired = isEmailChanged() ? emailVerification.isVerified : (emailVerification.isVerified || !email);
    const nameValid = formData.name && (validation.name.isValid || !validation.name.message);
    
    return (
      formData.id &&
      validation.id.isValid &&
      validation.password.isValid &&
      validation.confirmPassword.isValid &&
      nameValid &&
      formData.emailId &&
      formData.emailDomain &&
      emailVerificationRequired &&
      getCombinedPhone().length === 11 &&
      validation.phone.isValid &&
      validation.studentId.isValid
    );
  };

  return (
    <Container>
      <Header>
        <Logo onClick={() => navigate('/')}><img src="/images/global/logo.svg" alt="logo" /></Logo>
        <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
      </Header>
      <Content>
        <Title>회원가입</Title>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <LabelText htmlFor="id">
              아이디 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="text"
                placeholder="8글자 이상의 아이디를 입력하세요."
                value={formData.id}
                onChange={(e) => handleIdChange(e.target.value)}
                onBlur={handleIdBlur}
                $hasError={!!formData.id && !validation.id.isValid && !validation.id.isChecking}
                id="id"
              />
            </InputGroup>
            {formData.id && validation.id.message && (
              <ValidationMessage $isError={!validation.id.isValid}>
                {validation.id.message}
              </ValidationMessage>
            )}
          </FormField>

          <FormField>
            <LabelText htmlFor="password">
              비밀번호 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="password"
                placeholder="비밀번호를 입력하세요"
                value={formData.password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                $hasError={!!formData.password && !validation.password.isValid}
                id="password"
              />
              <Input
                type="password"
                placeholder="비밀번호를 한번 더 입력하세요"
                value={formData.confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                $hasError={!!formData.confirmPassword && !validation.confirmPassword.isValid}
              />
              {formData.password && !validation.password.isValid && validation.password.message ? (
                <ValidationMessage $isError>
                  {validation.password.message}
                </ValidationMessage>
              ) : formData.confirmPassword && validation.confirmPassword.message ? (
                <ValidationMessage $isError={!validation.confirmPassword.isValid}>
                  {validation.confirmPassword.message}
                </ValidationMessage>
              ) : formData.password && validation.password.message ? (
                <ValidationMessage $isError={false}>
                  {validation.password.message}
                </ValidationMessage>
              ) : (
                <ValidationMessage $isError={false} style={{ color: '#223BD8' }}>
                  영문,숫자,특수문자 포함 8자 이상 입력
                </ValidationMessage>
              )}
            </InputGroup>
          </FormField>

          <FormField>
            <LabelText htmlFor="name">
              이름 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="text"
                placeholder="이름을 입력하세요"
                value={formData.name}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData(prev => ({ ...prev, name: value }));
                  setValidation(prev => ({ ...prev, name: value ? validateName(value) : { isValid: false, message: '' } }));
                }}
                $hasError={!!formData.name && !validation.name.isValid && !!validation.name.message}
                id="name"
              />
            </InputGroup>
            {formData.name && validation.name.message && (
              <ValidationMessage $isError={!validation.name.isValid}>
                {validation.name.message}
              </ValidationMessage>
            )}
          </FormField>

          <FormField>
            <LabelText htmlFor="school">
              학교 <span className="required">*</span>
            </LabelText>
            <InputGroup className="row">
              <Input
                id="school"
                type="text"
                placeholder="학교를 검색하세요"
                value={formData.school}
                onChange={(e) => setFormData(prev => ({ ...prev, school: e.target.value }))}
                $hasError={false}
                readOnly
              />
              <SearchButton type="button" onClick={handleUniversitySearchClick}>
                <img src="/images/global/icon_search.svg" alt="search" />
              </SearchButton>
            </InputGroup>
          </FormField>

          <FormField>
            <LabelText htmlFor="department">
              학과 <span className="required">*</span>
            </LabelText>
            <InputGroup className="row">
              <Input
                type="text"
                placeholder="학과를 검색하세요"
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                $hasError={false}
                readOnly
                id="department"
              />
              <SearchButton type="button" onClick={handleDepartmentSearchClick}>
                <img src="/images/global/icon_search.svg" alt="search" />
              </SearchButton>
            </InputGroup>
          </FormField>

          <FormField>
            <LabelText htmlFor="studentId">
              학번 <span className="required">*</span>
            </LabelText>
            <InputGroup>
              <Input
                type="text"
                placeholder="학번을 입력하세요"
                value={formData.studentId}
                onChange={(e) => handleStudentIdChange(e.target.value)}
                $hasError={!!formData.studentId && !validation.studentId.isValid}
                id="studentId"
              />
            </InputGroup>
            {formData.studentId && validation.studentId.message && (
              <ValidationMessage $isError={!validation.studentId.isValid}>
                {validation.studentId.message}
              </ValidationMessage>
            )}
          </FormField>

          <FormField>
            <LabelText htmlFor="emailId">
              이메일 <span className="required">*</span>
            </LabelText>
            <InputGroup className="row Email">
              <Input
                id="emailId"
                type="text"
                placeholder="이메일 아이디"
                value={formData.emailId}
                style={{ width: '48%' }}
                onChange={(e) => {
                  const formatted = formatEmailInput(e.target.value);
                  setFormData(prev => ({ ...prev, emailId: formatted }));
                  // 이메일이 변경되면 인증 상태 초기화
                  if (emailVerification.isRequested || emailVerification.isVerified) {
                    if (countdownTimerRef.current) {
                      clearInterval(countdownTimerRef.current);
                      countdownTimerRef.current = null;
                    }
                    setEmailVerification({
                      requestUuid: '',
                      verificationCode: '',
                      isRequested: false,
                      isVerified: false,
                      isRequesting: false,
                      isVerifying: false,
                      error: '',
                      countdown: 0,
                      isExpired: false,
                    });
                    setOriginalEmail(''); // 원본 이메일 초기화
                  } else {
                    // 에러 메시지 초기화
                    setEmailVerification(prev => ({ ...prev, error: '' }));
                  }
                }}
                $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
              />
              <span>@</span>
              {emailDomainType === 'select' ? (
                <Select
                  style={{ width: '48%' }}
                  value={selectedDomainOption || formData.emailDomain || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    handleEmailDomainChange(e.target.value);
                    // 도메인이 변경되면 인증 상태 초기화
                    if (emailVerification.isRequested || emailVerification.isVerified) {
                      if (countdownTimerRef.current) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                      }
                      setEmailVerification({
                        requestUuid: '',
                        verificationCode: '',
                        isRequested: false,
                        isVerified: false,
                        isRequesting: false,
                        isVerifying: false,
                        error: '',
                        countdown: 0,
                        isExpired: false,
                      });
                      setOriginalEmail(''); // 원본 이메일 초기화
                    } else {
                      // 에러 메시지 초기화
                      setEmailVerification(prev => ({ ...prev, error: '' }));
                    }
                  }}
                  $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
                >
                  <option value="">도메인 선택</option>
                  {emailDomains.map((domain) => (
                    <option key={domain} value={domain}>
                      {domain}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  type="text"
                  placeholder="도메인 직접입력"
                  style={{ width: '48%' }}
                  value={formData.emailDomain}
                  onChange={(e) => {
                    const formatted = formatEmailInput(e.target.value);
                    setFormData(prev => ({ ...prev, emailDomain: formatted }));
                    // 도메인이 변경되면 인증 상태 초기화
                    if (emailVerification.isRequested || emailVerification.isVerified) {
                      if (countdownTimerRef.current) {
                        clearInterval(countdownTimerRef.current);
                        countdownTimerRef.current = null;
                      }
                      setEmailVerification({
                        requestUuid: '',
                        verificationCode: '',
                        isRequested: false,
                        isVerified: false,
                        isRequesting: false,
                        isVerifying: false,
                        error: '',
                        countdown: 0,
                        isExpired: false,
                      });
                    } else {
                      // 에러 메시지 초기화
                      setEmailVerification(prev => ({ ...prev, error: '' }));
                    }
                  }}
                  $hasError={!!emailVerification.error && emailVerification.error.includes('이미 사용중인 이메일')}
                />
              )}
            </InputGroup>
            <InputGroup style={{ position: 'relative' }}>
              {!emailVerification.isRequested && (
                <VerifyButton
                  type="button"
                  onClick={handleEmailVerificationRequest}
                  $disabled={!formData.emailId || !formData.emailDomain || emailVerification.isRequesting || emailVerification.isVerified}
                  $verified={false}
                  style={{ marginTop: '6px' }}
                >
                  {emailVerification.isRequesting ? '요청중...' : '이메일 인증하기'}
                </VerifyButton>
              )}
              {emailVerification.isRequested && !emailVerification.isVerified && emailVerification.countdown > 0 && (
                <VerifyTimer>
                  {formatTime(emailVerification.countdown)}
                </VerifyTimer>
              )}
              </InputGroup>
              
              {emailVerification.isRequested && !emailVerification.isVerified && (
                <InputGroup style={{ marginTop: '0.5rem' }}>
                  <Input
                    type="text"
                    placeholder="인증 코드 입력"
                    value={emailVerification.verificationCode}
                    onChange={(e) => setEmailVerification(prev => ({ ...prev, verificationCode: e.target.value, error: '' }))}
                    $hasError={!!emailVerification.error}
                    maxLength={6}
                  />
                  <VerifyButton
                    type="button"
                    onClick={handleEmailVerificationVerify}
                    $disabled={!emailVerification.verificationCode || emailVerification.isVerifying || emailVerification.isVerified}
                    $verified={emailVerification.isVerified}
                  >
                    {emailVerification.isVerifying ? '확인중...' : '인증확인'}
                  </VerifyButton>
                </InputGroup>
              )}
              {emailVerification.isVerified && (
                <InputGroup style={{ marginTop: '0.5rem' }}>
                  <VerifyButton
                    type="button"
                    $disabled={true}
                    $verified={true}
                  >
                    인증완료
                  </VerifyButton>
                </InputGroup>
              )}
            {emailVerification.error && (
              <ValidationMessage $isError={true}>
                {emailVerification.error}
              </ValidationMessage>
            )}
            {emailVerification.isVerified && (
              <ValidationMessage $isError={false}>
                이메일 인증이 완료되었습니다.
              </ValidationMessage>
            )}
          </FormField>

          <FormField>
            <LabelText htmlFor="phone">
              전화번호 <span className="required">*</span>
            </LabelText>
            <PhoneInputWrapper>
              <Input
                type="tel"
                placeholder="010"
                value={formData.phone1}
                onChange={(e) => handlePhonePartChange(1, e.target.value)}
                $hasError={!!getCombinedPhone() && !validation.phone.isValid}
                maxLength={3}
              />
              <span style={{ color: 'var(--text-secondary)' }}>-</span>
              <Input
                type="tel"
                placeholder="0000"
                value={formData.phone2}
                onChange={(e) => handlePhonePartChange(2, e.target.value)}
                $hasError={!!getCombinedPhone() && !validation.phone.isValid}
                maxLength={4}
              />
              <span style={{ color: 'var(--text-secondary)' }}>-</span>
              <Input
                type="tel"
                placeholder="0000"
                value={formData.phone3}
                onChange={(e) => handlePhonePartChange(3, e.target.value)}
                $hasError={!!getCombinedPhone() && !validation.phone.isValid}
                maxLength={4}
              />
            </PhoneInputWrapper>
            {getCombinedPhone() && validation.phone.message && (
              <ValidationMessage $isError={!validation.phone.isValid}>
                {validation.phone.message}
              </ValidationMessage>
            )}
          </FormField>

          <NextButton type="submit" $disabled={!isFormValid() || isSubmitting}>
            {isSubmitting ? '처리 중...' : '다음'}
          </NextButton>
        </Form>
      </Content>
      <ExpirationModal
        isOpen={showExpirationModal}
        onConfirm={handleResendVerification}
        onCancel={() => setShowExpirationModal(false)}
        title="인증 시간 만료"
        message="인증 유효 시간이 만료되었습니다.&#10;다시 인증 코드를 받아주세요."
        confirmText="다시 받기"
        cancelText="취소"
      />

      {showUniversityModal && (
        <ModalOverlay onClick={() => setShowUniversityModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalTitle>학교 선택</ModalTitle>
            <SearchInput
              type="text"
              placeholder="학교명 검색..."
              value={universitySearch}
              onChange={(e) => setUniversitySearch(e.target.value)}
            />
            <ListContainer>
              {isLoadingUniversities ? (
                <LoadingText>로딩 중...</LoadingText>
              ) : universityError ? (
                <ErrorText>{universityError}</ErrorText>
              ) : filteredUniversities.length === 0 ? (
                <LoadingText>검색 결과가 없습니다.</LoadingText>
              ) : (
                filteredUniversities.map((university) => (
                  <ListItem
                    key={university.university_no}
                    $selected={formData.universityNo === university.university_no}
                    onClick={() => handleUniversitySelect(university)}
                  >
                    {university.name}
                  </ListItem>
                ))
              )}
            </ListContainer>
            <ModalButtonGroup>
              <ModalButton onClick={() => setShowUniversityModal(false)}>
                취소
              </ModalButton>
            </ModalButtonGroup>
          </ModalContainer>
        </ModalOverlay>
      )}

      {showDepartmentModal && (
        <ModalOverlay onClick={() => setShowDepartmentModal(false)}>
          <ModalContainer onClick={(e) => e.stopPropagation()}>
            <ModalTitle>학과 선택</ModalTitle>
            <SearchInput
              type="text"
              placeholder="학과명 검색..."
              value={departmentSearch}
              onChange={(e) => setDepartmentSearch(e.target.value)}
            />
            <ListContainer>
              {isLoadingDepartments ? (
                <LoadingText>로딩 중...</LoadingText>
              ) : departmentError ? (
                <ErrorText>{departmentError}</ErrorText>
              ) : filteredDepartments.length === 0 ? (
                <LoadingText>검색 결과가 없습니다.</LoadingText>
              ) : (
                filteredDepartments.map((department) => (
                  <ListItem
                    key={department.department_no}
                    $selected={formData.departmentNo === department.department_no}
                    onClick={() => handleDepartmentSelect(department)}
                  >
                    {department.name}
                  </ListItem>
                ))
              )}
            </ListContainer>
            <ModalButtonGroup>
              <ModalButton onClick={() => setShowDepartmentModal(false)}>
                취소
              </ModalButton>
            </ModalButtonGroup>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default RegisterForm;
